/**
 * Indexable public surface for the current Folyamat (post–pre-Christ cleanup).
 * Sitemap, SSR shells, and SeoHead robots must all honor this allowlist.
 * Deferred marketing routes stay reachable but noindex until CEO re-activates them here.
 */

export const PUBLIC_INDEX_ROBOTS = "index,follow" as const;
export const PUBLIC_NOINDEX_ROBOTS = "noindex,nofollow" as const;

/** Exact paths Google may index (canonical marketing + legal). */
export const PUBLIC_INDEXABLE_EXACT_PATHS = [
  "/start",
  "/bekapcs",
  "/archivum",
  "/irasok",
  "/events",
  "/tamogatas",
  "/taska",
  "/hirlevel",
  "/kapcsolat",
  "/kapcsolattartasi-adatok",
  "/adatvedelem",
  "/aszf",
  "/sutik",
  "/visszaterites",
  "/jogi-kozlemeny",
  "/akadalymentesites",
  // Live event landings (also covered by /events/ prefix for future IDs).
  "/events/2026-04-18-egyeni-kezeles",
  "/events/2026-04-19-egyeni-kezeles",
] as const;

/** Dynamic public content prefixes that may be indexed when published. */
export const PUBLIC_INDEXABLE_PREFIXES = ["/p/", "/events/"] as const;

/** CEO-deferred: live for users later, but not indexable until re-added above. */
export const PUBLIC_DEFERRED_INDEX_PATHS = [
  "/alapok",
  "/gabor",
  "/kuldetes",
  "/rolunk",
  "/courses",
  "/kozosseg",
  "/mentor",
  "/bojt",
  "/ujdonsagok",
  "/ingyenes-tartalmak",
] as const;

const EXACT = new Set<string>(PUBLIC_INDEXABLE_EXACT_PATHS);

export const normalizePublicSeoPath = (pathname: string): string => {
  if (!pathname) return "/";
  const withSlash = pathname.startsWith("/") ? pathname : `/${pathname}`;
  if (withSlash.length > 1 && withSlash.endsWith("/")) {
    return withSlash.slice(0, -1);
  }
  return withSlash;
};

export const isPublicIndexablePath = (pathname: string): boolean => {
  const path = normalizePublicSeoPath(pathname);
  if (EXACT.has(path)) return true;
  if (path.endsWith("/success")) return false;
  return PUBLIC_INDEXABLE_PREFIXES.some(
    (prefix) => path.startsWith(prefix) && path.length > prefix.length,
  );
};

export const resolvePublicRobots = (pathname: string): typeof PUBLIC_INDEX_ROBOTS | typeof PUBLIC_NOINDEX_ROBOTS =>
  isPublicIndexablePath(pathname) ? PUBLIC_INDEX_ROBOTS : PUBLIC_NOINDEX_ROBOTS;
